import {
    extractHourFromCreateTime,
    getCleaningModeLabel,
    getCleaningReportDayKey,
    getCleaningReportTaskStartTime,
    hasMeasuredConsumptionValue,
} from './cleaningReportUtils';

export const BATTERY_MODE_ALL = 'all';
export const BATTERY_MODE_DRY = '2';
export const BATTERY_MODE_WET = '1';

function round1(n) {
    return Math.round(Number(n) * 10) / 10;
}

export function calcMinCost(costs) {
    if (!Array.isArray(costs) || costs.length === 0) return null;
    const positiveCosts = costs.filter(c => Number(c) > 0);
    return round1(
        positiveCosts.length ? Math.min(...positiveCosts) : Math.min(...costs)
    );
}

function pad2(n) {
    return String(n).padStart(2, '0');
}

/**
 * Delta de carga da missão (`cost_battery`). Não usar `battery` (SOC no fim).
 * Pudu: "electricity consumption is measured as a battery level percentage difference".
 */
export function getTaskBatteryCost(row) {
    if (!row || typeof row !== 'object') return null;
    if (!hasMeasuredConsumptionValue(row.cost_battery)) return null;
    return Number(row.cost_battery);
}

export function matchesBatteryMode(row, modeFilter) {
    if (modeFilter == null || modeFilter === BATTERY_MODE_ALL) return true;
    return String(Number(row && row.mode)) === String(Number(modeFilter));
}

export function missionsWithBattery(list, modeFilter = BATTERY_MODE_ALL) {
    return (Array.isArray(list) ? list : []).filter(row => {
        if (getTaskBatteryCost(row) == null) return false;
        return matchesBatteryMode(row, modeFilter);
    });
}

export function formatMapLabel(map, unknownLabel = '—') {
    if (!map || typeof map !== 'object') return unknownLabel;
    const name = map.map_name != null ? String(map.map_name).trim() : '';
    const floor = map.map_floor != null ? String(map.map_floor).trim() : '';
    let pretty = name;
    if (name.includes('#')) {
        const parts = name.split('#').filter(Boolean);
        pretty = parts[parts.length - 1] || name;
    }
    if (pretty && floor && pretty !== floor) {
        return `${pretty} (${floor})`;
    }
    return pretty || floor || unknownLabel;
}

function taskNameFallback(row, unknownLabel) {
    const taskName =
        row && row.task_name != null ? String(row.task_name).trim() : '';
    if (!taskName) {
        return { key: '', label: unknownLabel };
    }
    return {
        key: `task:${taskName}`,
        label: formatMapLabel({ map_name: taskName }, unknownLabel),
    };
}

/**
 * Uma missão, um mapa: o andar com maior área limpa.
 * A Pudu não quebra cost_battery por andar. Sem floor_list, usa task_name.
 */
export function getTaskPrimaryMap(row, unknownLabel = '—') {
    const maps = Array.isArray(row && row.maps) ? row.maps : [];
    if (!maps.length) {
        return taskNameFallback(row, unknownLabel);
    }
    let best = maps[0];
    maps.forEach(map => {
        const area = Number(map && map.area) || 0;
        const bestArea = Number(best && best.area) || 0;
        if (area > bestArea) best = map;
    });
    const label = formatMapLabel(best, unknownLabel);
    const key = `${best.map_name || ''}|${best.map_floor || ''}` || label;
    return { key, label, map: best };
}

function robotKey(row) {
    if (row && row._robot && row._robot.id) return String(row._robot.id);
    return String(
        (row && (row.sn || row.device_sn || row.robot_code)) || ''
    ).trim();
}

function robotLabel(row) {
    if (row && row._robot) {
        const name = row._robot.name || '';
        const code = row._robot.code ? ` (${row._robot.code})` : '';
        const combined = `${name}${code}`.trim();
        if (combined) return combined;
    }
    return robotKey(row) || '—';
}

export function buildBatteryKpis(list, modeFilter = BATTERY_MODE_ALL) {
    const rows = missionsWithBattery(list, modeFilter);
    const empty = {
        count: 0,
        avg: null,
        min: null,
        max: null,
        efficiency: null,
        totalArea: 0,
    };
    if (!rows.length) return empty;

    const costs = rows.map(getTaskBatteryCost);
    const sumCost = costs.reduce((s, n) => s + n, 0);
    let area = 0;
    rows.forEach(row => {
        area += Number(row.clean_area) || 0;
    });

    return {
        count: rows.length,
        avg: round1(sumCost / rows.length),
        min: calcMinCost(costs),
        max: round1(Math.max(...costs)),
        efficiency: area > 0 ? round1((sumCost / area) * 100) : null,
        totalArea: area,
    };
}

export function groupTopMapsAndOthers(
    maps,
    limit = 5,
    othersLabel = 'Outros mapas'
) {
    if (!Array.isArray(maps) || maps.length === 0) return [];
    if (!limit || maps.length <= limit) return maps;

    const head = maps.slice(0, limit);
    const tail = maps.slice(limit);
    const costs = [];
    let tasks = 0;
    tail.forEach(row => {
        tasks += Number(row.tasks) || 0;
        if (row.avg != null) {
            costs.push(row.avg);
        }
    });
    const sumAvg = tail.reduce(
        (s, row) => s + (Number(row.avg) || 0) * (Number(row.tasks) || 0),
        0
    );
    const avg = tasks > 0 ? round1(sumAvg / tasks) : 0;
    head.push({
        key: '__others__',
        name: othersLabel,
        tasks,
        avg,
        min: calcMinCost(costs),
        max: costs.length ? round1(Math.max(...costs)) : null,
        others: true,
    });
    return head;
}

export function buildBatteryByMap(
    list,
    {
        modeFilter = BATTERY_MODE_ALL,
        unknownLabel = '—',
        othersLabel = '—',
        limit = 5,
    } = {}
) {
    const rows = missionsWithBattery(list, modeFilter);
    const buckets = {};
    rows.forEach(row => {
        const cost = getTaskBatteryCost(row);
        const { key, label } = getTaskPrimaryMap(row, unknownLabel);
        const id = key || label;
        if (!buckets[id]) {
            buckets[id] = { key: id, name: label, costs: [], tasks: 0 };
        }
        buckets[id].costs.push(cost);
        buckets[id].tasks += 1;
    });

    const ranked = Object.values(buckets)
        .map(bucket => {
            const sum = bucket.costs.reduce((s, n) => s + n, 0);
            return {
                key: bucket.key,
                name: bucket.name,
                tasks: bucket.tasks,
                avg: round1(sum / bucket.tasks),
                min: calcMinCost(bucket.costs),
                max: round1(Math.max(...bucket.costs)),
            };
        })
        .sort((a, b) => b.avg - a.avg);

    return groupTopMapsAndOthers(ranked, limit, othersLabel);
}

export function buildBatteryByMode(list, localeCode = 'pt_BR') {
    const rows = missionsWithBattery(list, BATTERY_MODE_ALL);
    const buckets = {
        2: { costs: [], tasks: 0 },
        1: { costs: [], tasks: 0 },
    };
    rows.forEach(row => {
        const mode = Number(row.mode);
        if (mode !== 1 && mode !== 2) return;
        buckets[mode].costs.push(getTaskBatteryCost(row));
        buckets[mode].tasks += 1;
    });

    return [2, 1].map(mode => {
        const bucket = buckets[mode];
        const sum = bucket.costs.reduce((s, n) => s + n, 0);
        return {
            mode,
            name: getCleaningModeLabel(mode, localeCode),
            tasks: bucket.tasks,
            avg: bucket.tasks ? round1(sum / bucket.tasks) : 0,
            min: bucket.tasks ? calcMinCost(bucket.costs) : null,
            max: bucket.tasks ? round1(Math.max(...bucket.costs)) : null,
        };
    });
}

export function buildBatteryByRobot(list, modeFilter = BATTERY_MODE_ALL) {
    const rows = missionsWithBattery(list, modeFilter);
    const buckets = {};
    rows.forEach(row => {
        const id = robotKey(row);
        if (!id) return;
        if (!buckets[id]) {
            buckets[id] = {
                robot_id: id,
                name: robotLabel(row),
                costs: [],
                area: 0,
                tasks: 0,
            };
        }
        buckets[id].costs.push(getTaskBatteryCost(row));
        buckets[id].area += Number(row.clean_area) || 0;
        buckets[id].tasks += 1;
    });

    return Object.values(buckets)
        .map(bucket => {
            const sum = bucket.costs.reduce((s, n) => s + n, 0);
            return {
                robot_id: bucket.robot_id,
                name: bucket.name,
                tasks: bucket.tasks,
                avg: round1(sum / bucket.tasks),
                efficiency:
                    bucket.area > 0 ? round1((sum / bucket.area) * 100) : null,
            };
        })
        .sort((a, b) => b.avg - a.avg);
}

export function mergeBatteryIntoRobotComparison(
    data,
    list,
    modeFilter = BATTERY_MODE_ALL
) {
    if (!Array.isArray(data) || !data.length) return data || [];
    const byId = {};
    buildBatteryByRobot(list, modeFilter).forEach(row => {
        byId[String(row.robot_id)] = row;
    });
    return data.map(item => {
        const row =
            byId[String(item.robot_id)] || byId[String(item.sn)] || null;
        return {
            ...item,
            battery_avg: row ? row.avg : 0,
            battery_per_100m2:
                row && row.efficiency != null ? row.efficiency : 0,
        };
    });
}

export function formatDayKeyToDmy(key) {
    if (key == null || typeof key !== 'string') return key;
    const trimmed = key.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const [y, m, d] = trimmed.split('-');
        return `${d}-${m}-${y}`;
    }
    if (/^\d{4}-\d{2}$/.test(trimmed)) {
        const [y, m] = trimmed.split('-');
        return `${m}-${y}`;
    }
    return key;
}

export function buildBatteryByPeriod(
    list,
    { modeFilter = BATTERY_MODE_ALL, periodDates = null } = {}
) {
    const rows = missionsWithBattery(list, modeFilter);
    if (!rows.length) return { unit: 'day', data: [] };

    const start = periodDates && periodDates.startDate;
    const end = periodDates && periodDates.endDate;
    const isSingleDay = !!(start && end && start === end);
    let spanDays = 0;
    if (start && end) {
        const a = new Date(`${start}T12:00:00`);
        const b = new Date(`${end}T12:00:00`);
        if (!Number.isNaN(a.getTime()) && !Number.isNaN(b.getTime())) {
            spanDays = Math.round((b - a) / 86400000) + 1;
        }
    }
    const useMonth = !isSingleDay && spanDays > 62;

    if (isSingleDay) {
        const byHour = new Array(24).fill(null).map(() => ({ sum: 0, n: 0 }));
        rows.forEach(row => {
            const hour = extractHourFromCreateTime(
                getCleaningReportTaskStartTime(row)
            );
            if (hour == null) return;
            byHour[hour].sum += getTaskBatteryCost(row);
            byHour[hour].n += 1;
        });
        return {
            unit: 'hour',
            data: byHour.map((bucket, hour) => ({
                date: `${pad2(hour)}h`,
                avg: bucket.n ? round1(bucket.sum / bucket.n) : 0,
                tasks: bucket.n,
            })),
        };
    }

    const buckets = {};
    rows.forEach(row => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(row)
        );
        if (!day || day === '—') return;
        const key = useMonth ? day.slice(0, 7) : day;
        if (!buckets[key]) buckets[key] = { sum: 0, n: 0 };
        buckets[key].sum += getTaskBatteryCost(row);
        buckets[key].n += 1;
    });

    const keys = Object.keys(buckets).sort();
    return {
        unit: useMonth ? 'month' : 'day',
        data: keys.map(key => ({
            date: formatDayKeyToDmy(key),
            rawDate: key,
            avg: round1(buckets[key].sum / buckets[key].n),
            tasks: buckets[key].n,
        })),
    };
}
